import { useMemo, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { BoolNode } from 'src/services/common.types';

import Iconify from 'src/components/iconify';

import { SearchFormValues } from '../types/search.types';
import AdvancedSearchResults from '../components/advanced-search-results';
import CandidateSearchFilters from '../components/candidate-search-filters';

type Props = {
  jobId?: string;
};

const EMPTY_DSL: BoolNode = { op: 'AND', children: [] };

/**
 * Advanced Search (JT1-1532, items 23/24/32). Collapsible filters (keyword + structured builder) on
 * top; results as a full-width shared DataList below (AdvancedSearchResults), routed through the lean
 * path so they behave like every candidate list — columns, per-column search, sort, infinite scroll,
 * and the record panel. Rendered both as the standalone "Advanced Search" tab and embedded in a job
 * for sourcing (jobId drives the bulk "Add to job").
 */
export default function CandidateSearchView({ jobId }: Props) {
  const { t } = useTranslate();
  const filtersOpen = useBoolean(true);

  const [searchDsl, setSearchDsl] = useState<BoolNode>(EMPTY_DSL);
  const [keyword, setKeyword] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // The filters own their builder state; a stable empty initial keeps them from resetting each render.
  const filterInitial = useMemo(() => ({}), []);

  const onSearch = useCallback((values: SearchFormValues) => {
    setSearchDsl(values.search_dsl ?? EMPTY_DSL);
    setKeyword(values.keyword ?? '');
    setHasSearched(true);
    filtersOpen.onFalse();
  }, [filtersOpen]);

  const onClearFilters = useCallback(() => {
    setSearchDsl(EMPTY_DSL);
    setKeyword('');
    setHasSearched(false);
    filtersOpen.onTrue();
  }, [filtersOpen]);

  return (
    <Stack spacing={2}>
      <Card sx={{ borderRadius: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="h6">{t('candidates.advanced_search', { defaultValue: 'Advanced Search' })}</Typography>
          <Button
            size="small"
            color="inherit"
            onClick={filtersOpen.onToggle}
            startIcon={<Iconify icon={filtersOpen.value ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} />}
          >
            {filtersOpen.value
              ? t('candidates.search.hide_filters', { defaultValue: 'Hide filters' })
              : t('candidates.search.show_filters', { defaultValue: 'Show filters' })}
          </Button>
        </Stack>
        <Collapse in={filtersOpen.value}>
          <Box sx={{ px: 3, pb: 3 }}>
            <CandidateSearchFilters onSearch={onSearch} onClearFilters={onClearFilters} initialValues={filterInitial} />
          </Box>
        </Collapse>
      </Card>

      <AdvancedSearchResults searchDsl={searchDsl} keyword={keyword} hasSearched={hasSearched} jobId={jobId} />
    </Stack>
  );
}
